/**
 * The searchable IANA timezone list for the profile and organization
 * settings selectors.
 *
 * <p>Uses {@code Intl.supportedValuesOf('timeZone')} when available (all
 * evergreen browsers since 2022). Each entry carries its CURRENT UTC offset
 * and the list is sorted by offset so a region's zones sit together.
 * Offsets drift across DST, so the cache is rebuilt at most hourly.
 *
 * <p>"UTC" is added explicitly: Chrome's supportedValuesOf list contains only
 * region zones, so without this nobody could pick plain UTC.
 */
import { browserTimezone } from './resolveTimezone';

export { browserTimezone };

const RECOMPUTE_INTERVAL_MS = 60 * 60 * 1000;
let _cache = null;
let _cachedAtMs = 0;

/** Curated fallback list when Intl.supportedValuesOf is unavailable. */
const FALLBACK_TZS = [
  'UTC',
  'America/Anchorage', 'America/Los_Angeles', 'America/Denver',
  'America/Chicago', 'America/New_York', 'America/Sao_Paulo',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Moscow',
  'Africa/Cairo', 'Africa/Johannesburg',
  'Asia/Amman', 'Asia/Dubai', 'Asia/Karachi', 'Asia/Kolkata', 'Asia/Dhaka',
  'Asia/Bangkok', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Tokyo',
  'Australia/Sydney', 'Pacific/Auckland',
];

/**
 * Parse a "GMT+5:30" / "GMT-8" / "GMT" string from
 * {@code Intl.DateTimeFormat} into a zero-padded "±HH:MM".
 */
const padOffsetStr = (s) => {
  if (!s) return '+00:00';
  const m = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(s);
  if (!m) return '+00:00';
  const sign = m[1];
  const hh = String(parseInt(m[2], 10)).padStart(2, '0');
  const mm = (m[3] || '00').padStart(2, '0');
  return `${sign}${hh}:${mm}`;
};

const offsetMinutes = (offsetStr) => {
  const m = /([+-])(\d{2}):(\d{2})/.exec(offsetStr);
  if (!m) return 0;
  return (m[1] === '-' ? -1 : 1) * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10));
};

/** Current UTC offset of `tz` as "±HH:MM" ("+00:00" when unknown). */
export const offsetForTz = (tz, at = new Date()) => {
  try {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    }).formatToParts(at);
    const tzName = parts.find((p) => p.type === 'timeZoneName')?.value;
    return padOffsetStr(tzName);
  } catch (_) {
    return '+00:00';
  }
};

/** One selector entry for `tz`. */
export const timezoneOption = (tz) => {
  const offset = offsetForTz(tz);
  return {
    value: tz,
    offset,
    offsetMins: offsetMinutes(offset),
    label: `(UTC${offset}) ${tz.replace(/_/g, ' ')}`,
  };
};

/**
 * Sorted list of timezone options:
 * {@code [{ value: 'Asia/Kolkata', label: '(UTC+05:30) Asia/Kolkata', offset: '+05:30', offsetMins: 330 }, ...]}.
 * Sorted ascending by offset, then by IANA name.
 */
export const getTimezoneOptions = () => {
  const now = Date.now();
  if (_cache && now - _cachedAtMs < RECOMPUTE_INTERVAL_MS) return _cache;

  let names = [];
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      names = Intl.supportedValuesOf('timeZone') || [];
    }
  } catch (_) {
    /* fall through */
  }
  if (names.length === 0) names = FALLBACK_TZS;
  if (!names.includes('UTC')) names = ['UTC', ...names];

  const items = names.map(timezoneOption);
  items.sort((a, b) => a.offsetMins - b.offsetMins || a.value.localeCompare(b.value));
  _cache = items;
  _cachedAtMs = now;
  return items;
};

/**
 * The "inherit" entry pinned first in the PROFILE selector. Picking it clears
 * the user's own zone (value '') so they follow the organization default —
 * or, when the org has none, whatever this browser detects.
 */
export const browserDefaultOption = (orgTz) => {
  const tz = orgTz || browserTimezone();
  return {
    value: '',
    resolved: tz,
    offset: offsetForTz(tz),
    label: orgTz
      ? `Use organization default (${tz}, UTC${offsetForTz(tz)})`
      : `Use organization default (none set — detected ${tz}, UTC${offsetForTz(tz)})`,
    isBrowserDefault: true,
    isOrgDefault: !!orgTz,
  };
};
